import { lazy, Suspense, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import RatingStars from '@/components/reviews/RatingStars';
import useNearViewport from '@/hooks/useNearViewport';
const GoogleRatingDialog = lazy(() => import('@/components/reviews/GoogleRatingDialog'));

export default function GoogleRatingBadge({ en = false, compact = false }) {
  const [open, setOpen] = useState(false);
  const [badgeRef, nearViewport] = useNearViewport({ rootMargin: '150px' });
  const { data, isPending } = useQuery({
    enabled: nearViewport,
    queryKey: ['google-store-rating', 'DK'],
    queryFn: async () => (await base44.functions.invoke('googleStoreRating', {})).data,
    staleTime: 3600000, refetchInterval: 3600000, retry: false,
  });
  const label = compact ? (en ? 'on Google' : 'på Google') : (en ? 'Google store rating' : 'Butiksbedømmelse på Google');
  const locale = en ? 'en-GB' : 'da-DK';
  const style = 'google-rating-badge inline-flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full border border-ee-line bg-ee-surface px-4 py-2.5 text-sm font-body text-ee-ink hover:bg-ee-canvas';
  if (!data?.available) return <a ref={badgeRef} href={`https://www.google.com/storepages?q=enkelenergi.dk&c=DK&hl=${en ? 'en' : 'da'}#reviews`} target="_blank" rel="noopener noreferrer" className={style}>
    {isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ExternalLink className="h-4 w-4" aria-hidden="true" />}{label}
  </a>;
  const score = data.rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return <>
    <button ref={badgeRef} type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open} className={style}>
      <RatingStars rating={data.rating} /><strong>{compact ? `${score} ${en ? 'out of 5' : 'ud af 5'}` : `${score}/5`}</strong><span>{label}</span>
      {!compact && <><span className="text-ee-muted">· {data.reviewCount.toLocaleString(locale)} {en ? 'reviews' : 'anmeldelser'}</span><ChevronRight className="h-4 w-4" aria-hidden="true" /></>}
    </button>
    {open && <Suspense fallback={<span role="status" className="sr-only">{en ? 'Loading reviews' : 'Henter anmeldelser'}</span>}><GoogleRatingDialog open={open} onOpenChange={setOpen} data={data} en={en} /></Suspense>}
  </>;
}